import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ListGroup } from 'reactstrap';
import {
  camelCase,
  orderBy,
  tail,
  uniqBy,
} from 'lodash';
import { useFeatures } from '@cordata/buffet';
import { useParams } from 'react-router-dom';

import SideNavItem from './SideNavItem';

const SideNavState = 'isSideCollapsed';
const getIsSideCollapsed = () => {
  const sideNavState = localStorage.getItem(SideNavState) === 'true';
  return sideNavState;
};

const groupChildrenUnderParents = (items) => {
  // group child items under parent items, allowing for multiple tiers of
  // nesting by starting at the bottom tier and going up

  const isOrphan = (itemList, item) =>
    item.parent && // item has a parent
    !itemList.some(i => i.parent === item.navItemName) && // nothing else has it as a parent
    itemList.some(i => i.navItemName === item.parent); // the parent exists in the list

  const hasOrphans = itemList => itemList.some(i => isOrphan(itemList, i));

  const moveChildToParent = (itemList, childIndex) => {
    itemList.find(ii =>
      ii.navItemName === itemList[childIndex].parent).items.unshift(itemList[childIndex]);
    itemList.splice(childIndex, 1);
  };

  while (hasOrphans(items)) {
    // loop in reverse to avoid problems with removing items from array while looping over it
    /* eslint-disable-next-line no-plusplus */
    for (let i = items.length - 1; i >= 0; i--) {
      if (isOrphan(items, items[i])) {
        moveChildToParent(items, i);
      }
    }
  }

  const orderItems = (itemList) => {
    itemList.forEach((item) => {
      if (item.items.length) {
        /* eslint-disable-next-line no-param-reassign */
        item.items = orderItems(item.items);
      }
    });

    return orderBy(itemList, ['order', 'text']);
  };

  return orderItems(items);
};

const SideNav = ({ items }) => {
  const features = useFeatures();
  const params = useParams();

  const [transformedItems, setTransformedItems] = useState([]);
  const [isSideCollapsed, setIsSideCollapsed] = useState(getIsSideCollapsed());

  useEffect(() => {
    if (items?.length) {
      let transformed = items
        .map((item) => {
          const i = { attributes: item, ...item };

          const [route, dynamicSegment] = (i.attributes.route || '').split(':');
          const r = tail(route.split('.')).join('.');

          return {
            ...i.attributes,
            label: i.label || i.attributes.navItemLabel, // might need to transform this
            route: r,
            pathname: `/${i.attributes.appModule}${i.attributes.appModule === 'patient' ? `s/${params.patient_id}/${r}` : `${r.replace(i.attributes.appModule, '').replace(/\//, '')}`}`,
            dynamicSegment,
            order: i.attributes.defaultSortOrder,
            items: [],

            id: i.id,
            navItemName: i.attributes.navItemName,
            parent: i.attributes.parentNavItemName,
          };
        })
        .filter((i) => {
          if (i.featureFlag && i.featureFlag.length) {
            return i.featureFlag.every(f => (f.startsWith('!') ? !features[camelCase(f.slice(1))] : !!features[camelCase(f)]));
          }

          return true;
        });

      transformed = uniqBy(transformed, i => [i.parent, i.label.toLowerCase()]);

      transformed = groupChildrenUnderParents(transformed);

      setTransformedItems(transformed);
    }
  }, [items, features]);

  return (
    <>
      <div className="trigger-collapse">

        <button
          className="side-nav-toggler"
          type="button"
          onClick={() => setIsSideCollapsed((prev) => !prev)}
        >
          <span className="side-nav-item">
            {isSideCollapsed ? 'Show Menu' : 'Hide Menu'}

          </span>

        </button>
        <ListGroup flush className={isSideCollapsed ? 'mobile-menu' : 'side-nav'}>
          {transformedItems.map(i => <SideNavItem key={`${i.pathname}${i.queryParams ? JSON.stringify(i.queryParams) : ''}${Math.random()}`} item={i} />)}

        </ListGroup>
      </div>
    </>
  );
};

SideNav.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape()).isRequired,
};

export default SideNav;
